import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { templeMistPosition } from './templeMist';

const vertex = `precision highp float;
attribute vec3 position; uniform mat4 worldViewProjection; varying vec3 p;
void main(){p=position;gl_Position=worldViewProjection*vec4(position,1.);}`;
const noise = `float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){return .5*noise(p)+.25*noise(p*2.03)+.125*noise(p*4.01)+.0625*noise(p*8.02);}`;

/** Nearby geometry supplies parallax; the photographic dome carries the distant detail. */
export function createLandscape(scene: Scene, parent: TransformNode | null) {
  const height = (x: number, z: number) => {
    const r = Math.hypot(x, z + 0.7);
    const mountain = 25 * Math.exp(-Math.max(0, r - 4.1) / 9);
    const hills = Math.sin(x * 0.11) * Math.cos(z * 0.09) * 2.2 + Math.sin(z * 0.2 + x * 0.15);
    const lake = Math.exp(-((x + 25) ** 2 / 350 + (z + 46) ** 2 / 650));
    return r < 4.1 ? -0.31 : -25.3 + mountain + hills * Math.min(1, (r - 4.1) / 12) - lake * 4;
  };
  const terrain = MeshBuilder.CreateGround(
    'mountainLandscape',
    { width: 190, height: 190, subdivisions: 100, updatable: true },
    scene,
  );
  const positions = terrain.getVerticesData(VertexBuffer.PositionKind)!;
  const colors: number[] = [];
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i]!,
      z = positions[i + 2]!;
    positions[i + 1] = height(x, z);
    const rocky = Math.hypot(x, z + 0.7) < 19;
    const grain = 0.85 + Math.sin(x * 7.1 + z * 4.9) * 0.1;
    const c = rocky ? [0.42, 0.43, 0.35] : [0.24, 0.38, 0.16];
    colors.push(c[0]! * grain, c[1]! * grain, c[2]! * grain, 1);
  }
  terrain.updateVerticesData(VertexBuffer.PositionKind, positions);
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, terrain.getIndices()!, normals);
  terrain.updateVerticesData(VertexBuffer.NormalKind, normals);
  terrain.setVerticesData(VertexBuffer.ColorKind, colors);
  const earth = new StandardMaterial('mountainStoneAndGrass', scene);
  const texture = new Texture('/textures/temple/marble.png', scene);
  texture.uScale = texture.vScale = 30;
  earth.diffuseTexture = texture;
  earth.specularColor = Color3.Black();
  terrain.material = earth;
  terrain.parent = parent;
  terrain.isPickable = false;

  // Two merged foliage layers: variation and tree silhouettes without hundreds of draw calls.
  const trees: Mesh[] = [];
  for (let i = 0; i < 150; i++) {
    const x = Math.sin(i * 127.1) * 77,
      z = Math.sin(i * 311.7) * 77;
    const y = height(x, z);
    if (Math.hypot(x, z) < 22 || y < -26) continue;
    const size = 0.6 + (Math.sin(i * 5.7) + 1) * 0.45;
    const tree = MeshBuilder.CreateCylinder(
      'distantPine',
      { diameterBottom: size, diameterTop: 0, height: size * 2.8, tessellation: 7 },
      scene,
    );
    tree.position.set(x, y + size * 1.4, z);
    trees.push(tree);
  }
  const forest = Mesh.MergeMeshes(trees, true, true)!;
  forest.name = 'mountainForest';
  const leaves = new StandardMaterial('forestGreen', scene);
  leaves.diffuseColor = new Color3(0.13, 0.27, 0.16);
  leaves.specularColor = Color3.Black();
  forest.material = leaves;
  forest.parent = parent;
  forest.isPickable = false;

  const water = new ShaderMaterial(
    'livingWater',
    scene,
    {
      vertexSource: vertex,
      fragmentSource: `precision highp float; varying vec3 p; uniform float time; ${noise}
    void main(){float n=fbm(p.xz*1.8+vec2(time*.09,time*.04));float glint=pow(max(0.,sin(p.x*7.+p.z*3.+n*8.+time*.8)),24.);gl_FragColor=vec4(mix(vec3(.035,.23,.25),vec3(.20,.52,.53),n)+glint*.12,1.);}`,
    },
    { attributes: ['position'], uniforms: ['worldViewProjection', 'time'] },
  );
  const lake = MeshBuilder.CreateDisc(
    'mountainLake',
    { radius: 1, tessellation: 64, sideOrientation: Mesh.DOUBLESIDE },
    scene,
  );
  lake.rotation.x = Math.PI / 2;
  lake.scaling.set(15, 23, 1);
  lake.position.set(-25, -26.2, -46);
  // Shader coordinates are local: retain useful wave variation across the lake.
  lake.bakeCurrentTransformIntoVertices();
  lake.material = water;
  lake.parent = parent;
  lake.isPickable = false;
  const banks: Vector3[][] = [[], []];
  for (let i = 0; i < 45; i++) {
    const z = -28 + i * 2.7,
      x = -27 + Math.sin(i * 0.19) * 10;
    const y = height(x, z) + 0.14;
    banks[0]!.push(new Vector3(x - 0.65, y, z));
    banks[1]!.push(new Vector3(x + 0.65, y, z));
  }
  const river = MeshBuilder.CreateRibbon(
    'flowingRiver',
    { pathArray: banks, sideOrientation: Mesh.DOUBLESIDE },
    scene,
  );
  river.material = water;
  river.parent = parent;
  river.isPickable = false;

  const clouds = MeshBuilder.CreateSphere(
    'movingClouds',
    { diameter: 280, segments: 24, sideOrientation: Mesh.BACKSIDE },
    scene,
  );
  const cloudMaterial = new ShaderMaterial(
    'cloudVeil',
    scene,
    {
      vertexSource: vertex,
      fragmentSource: `precision highp float; varying vec3 p; uniform float time; ${noise}
    void main(){vec3 d=normalize(p);float a=smoothstep(.50,.74,fbm(d.xz*7.+vec2(time*.003,0.)))*smoothstep(.05,.25,d.y)*.55;gl_FragColor=vec4(.95,.97,1.,a);}`,
    },
    {
      attributes: ['position'],
      uniforms: ['worldViewProjection', 'time'],
      needAlphaBlending: true,
    },
  );
  cloudMaterial.backFaceCulling = false;
  cloudMaterial.disableDepthWrite = true;
  clouds.material = cloudMaterial;
  clouds.parent = parent;
  clouds.isPickable = false;

  const mistTexture = new DynamicTexture('lakeMistTexture', 128, scene, false);
  const ctx = mistTexture.getContext() as CanvasRenderingContext2D;
  const gradient = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(229,241,244,0.8)');
  gradient.addColorStop(1, 'rgba(229,241,244,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 128, 128);
  mistTexture.hasAlpha = true;
  mistTexture.update();
  const mistMaterial = (name: string) => {
    const material = new StandardMaterial(name, scene);
    material.diffuseTexture = mistTexture;
    material.opacityTexture = mistTexture;
    material.disableLighting = true;
    material.emissiveColor = new Color3(0.8, 0.9, 0.92);
    material.backFaceCulling = false;
    material.disableDepthWrite = true;
    return material;
  };
  const mist = mistMaterial('lakeMist');
  const fogs = Array.from({ length: 3 }, (_, i) => {
    const mesh = MeshBuilder.CreatePlane('lakeFog', { width: 28, height: 17 }, scene);
    mesh.rotation.x = Math.PI / 2;
    mesh.position.set(-25 + i * 2, -24.8 + i * 0.45, -47 + i * 3);
    mesh.material = mist;
    mesh.parent = parent;
    mesh.isPickable = false;
    return mesh;
  });
  // Low-cost cloud banks at temple altitude, not only fog far down over the lake. Eight quads
  // sharing the lake's texture: a cloned material had taken a copy of it, and a copied dynamic
  // texture is blank and never ready, so the banks were never drawn and the scene never ready.
  const highMist = mistMaterial('templeCloudMist');
  highMist.alpha = 0.55;
  const templeClouds = Array.from({ length: 8 }, (_, i) => {
    const mesh = MeshBuilder.CreatePlane('templeCloudBank', { width: 18, height: 6 }, scene);
    mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    mesh.material = highMist;
    mesh.parent = parent;
    mesh.isPickable = false;
    const p = templeMistPosition(i, 0);
    mesh.position.set(p.x, p.y, p.z);
    return mesh;
  });
  return {
    /**
     * The veil of moving clouds and the mist banks on or off. Off in the headset: the veil is a
     * noise shader over nearly every sky pixel, a post-process by another name at 72 Hz, and the
     * banks are blended quads metres across, drawn over and over (the backdrop has its own clouds).
     */
    setClouds(on: boolean) {
      clouds.setEnabled(on);
      for (const bank of [...fogs, ...templeClouds]) bank.setEnabled(on);
    },
    update(seconds: number) {
      water.setFloat('time', seconds);
      cloudMaterial.setFloat('time', seconds);
      templeClouds.forEach((cloud, i) => {
        const p = templeMistPosition(i, seconds);
        cloud.position.set(p.x, p.y, p.z);
      });
      fogs.forEach((fog, i) => {
        fog.position.x = -25 + i * 2 + Math.sin(seconds * 0.012 + i) * 2;
      });
    },
  };
}
